import React, { useState, useRef, useCallback, useEffect } from "react";
import Button from "./Button";
import Card from "./Card";
import Badge from "./Badge";
import ResponsivePanel from "../Layout/ResponsivePanel";
import { Users, Target, Trash2, Copy, TrendingUp, Search, Settings } from "lucide-react";

const nodeTypes = [
  // Input Modules
  {
    type: "audience",
    title: "Audience Selector",
    description: "Define your target audience",
    icon: Users,
    color: "bg-blue-50 border-blue-200",
    inputs: [],
    outputs: ["audience"],
    category: "Input",
  },
  {
    type: "brand",
    title: "Brand Context",
    description: "Set brand guidelines and voice",
    icon: Target,
    color: "bg-purple-50 border-purple-200",
    inputs: [],
    outputs: ["brand"],
    category: "Input",
  },
  {
    type: "competitor",
    title: "Competitor Analysis",
    description: "Analyze competitor content",
    icon: TrendingUp,
    color: "bg-red-50 border-red-200",
    inputs: [],
    outputs: ["competitor-data"],
    category: "Input",
  },
  {
    type: "keyword-research",
    title: "Keyword Research",
    description: "Research trending keywords",
    icon: Search,
    color: "bg-emerald-50 border-emerald-200",
    inputs: ["audience", "competitor-data"],
    outputs: ["keywords"],
    category: "Input",
  },
  // ... (add more nodeTypes as needed) ...
];

const createDemoWorkflow = () => {
  const demoNodes = [
    { id: "audience-demo", type: "audience", title: "Audience Selector", description: "Define your target audience", icon: Users, position: { x: 50, y: 100 }, inputs: [], outputs: ["audience"], status: "idle", config: {} },
    { id: "competitor-demo", type: "competitor", title: "Competitor Analysis", description: "Analyze competitor content", icon: TrendingUp, position: { x: 50, y: 300 }, inputs: [], outputs: ["competitor-data"], status: "idle" },
    { id: "keyword-demo", type: "keyword-research", title: "Keyword Research", description: "Research trending keywords", icon: Search, position: { x: 350, y: 200 }, inputs: ["audience", "competitor-data"], outputs: ["keywords"], status: "idle" },
    { id: "brand-demo", type: "brand", title: "Brand Context", description: "Set brand guidelines and voice", icon: Target, position: { x: 50, y: 500 }, inputs: [], outputs: ["brand"], status: "idle", config: {} },
  ];
  const demoConnections = [
    { id: "conn-1", from: "audience-demo", to: "keyword-demo", fromOutput: "audience", toInput: "audience" },
    { id: "conn-2", from: "competitor-demo", to: "keyword-demo", fromOutput: "competitor-data", toInput: "competitor-data" },
    { id: "conn-3", from: "audience-demo", to: "brand-demo", fromOutput: "audience", toInput: "brand" },
  ];
  return { nodes: demoNodes, connections: demoConnections };
};

export default function WorkflowCanvas() {
  const [nodes, setNodes] = useState([]);
  const [, setConnections] = useState([]);
  const [, setSelectedNode] = useState(null);
  const [draggedNode, setDraggedNode] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [draggedModuleType, setDraggedModuleType] = useState(null);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const canvasRef = useRef(null);

  // Debug log

  // Drag and drop handlers for module library
  const handleModuleDragStart = (e, moduleType) => {
    setDraggedModuleType(moduleType);
    e.dataTransfer.effectAllowed = "copy";
  };
  const handleCanvasDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const handleCanvasDrop = (e) => {
    e.preventDefault();
    if (!draggedModuleType || !canvasRef.current) return;
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const position = {
      x: e.clientX - canvasRect.left - 100,
      y: e.clientY - canvasRect.top - 50,
    };
    const nodeType = nodeTypes.find((t) => t.type === draggedModuleType);
    if (!nodeType) return;
    const newNode = {
      id: `${draggedModuleType}-${Date.now()}`,
      type: draggedModuleType,
      title: nodeType.title,
      description: nodeType.description,
      icon: nodeType.icon,
      position,
      inputs: nodeType.inputs,
      outputs: nodeType.outputs,
      status: "idle",
      config: {},
    };
    setNodes((prev) => [...prev, newNode]);
    setDraggedModuleType(null);
  };

  // Node dragging handlers
  const handleNodeMouseDown = (e, nodeId) => {
    if (e.button !== 0) return; // Only left click
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setDraggedNode(nodeId);
    setSelectedNode(nodeId);
  };
  const handleNodeMouseMove = useCallback((e) => {
    if (!draggedNode || !canvasRef.current) return;
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const newX = e.clientX - canvasRect.left - dragOffset.x;
    const newY = e.clientY - canvasRect.top - dragOffset.y;
    setNodes((prev) => prev.map((node) =>
      node.id === draggedNode ? { ...node, position: { x: Math.max(0, newX), y: Math.max(0, newY) } } : node
    ));
  }, [draggedNode, dragOffset]);
  const handleNodeMouseUp = useCallback(() => {
    setDraggedNode(null);
  }, []);
  useEffect(() => {
    if (draggedNode) {
      document.addEventListener("mousemove", handleNodeMouseMove);
      document.addEventListener("mouseup", handleNodeMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleNodeMouseMove);
        document.removeEventListener("mouseup", handleNodeMouseUp);
      };
    }
  }, [draggedNode, handleNodeMouseMove, handleNodeMouseUp]);

  // Node actions
  const handleDeleteNode = (nodeId) => {
    setNodes((prev) => prev.filter((node) => node.id !== nodeId));
  };
  const handleDuplicateNode = (nodeId) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;
    const newNode = {
      ...node,
      id: `${node.type}-${Date.now()}`,
      position: { x: node.position.x + 40, y: node.position.y + 40 },
    };
    setNodes((prev) => [...prev, newNode]);
  };
  const handleConfigNode = (nodeId) => {
    alert(`Configure node: ${nodeId}`); // Placeholder for config modal
  };

  // Demo and clear
  const handleLoadDemo = () => {
    const demo = createDemoWorkflow();
    setNodes(demo.nodes);
    setConnections(demo.connections);
  };
  const handleClearAll = () => {
    setNodes([]);
    setConnections([]);
    setSelectedNode(null);
  };

  return (
    <div className="workflow-workbench flex h-screen bg-gray-50">
      {/* Top Header Bar */}
      <div className="workflow-header absolute top-0 left-0 right-0 z-40 bg-white border-b border-gray-200">
        <div className="workflow-header__inner flex items-center justify-between px-6 py-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <h1 className="workflow-header__title text-2xl font-bold text-gray-900">Workflow Builder</h1>
              <Badge className="bg-orange-50 text-orange-700 border-orange-200">{nodes.length} modules</Badge>
            </div>
          </div>
          <div className="workflow-header__actions flex items-center space-x-3">
            <Button className="workflow-mobile-library-toggle" onClick={() => setIsLibraryOpen(true)}>Modules</Button>
            <Button onClick={handleLoadDemo}>Load Demo</Button>
            <Button onClick={handleClearAll} disabled={nodes.length === 0}>Clear All</Button>
          </div>
        </div>
      </div>
      {/* Left Panel - Module Palette */}
      <ResponsivePanel open={isLibraryOpen} onClose={() => setIsLibraryOpen(false)} side="left" label="Workflow module library" className="workflow-module-panel">
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col mt-20">
        <div className="p-4 border-b border-gray-200 bg-white">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Module Library</h2>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
            <h4 className="font-medium text-orange-900 text-sm mb-2">How to Use:</h4>
            <ol className="text-xs text-orange-800 space-y-1">
              <li>Drag modules to canvas or click to add</li>
              <li>Click on output circles to connect</li>
              <li>Release anywhere on target module!</li>
              <li>Click gear icon to configure modules</li>
              <li>Click eye icon to view results</li>
            </ol>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {["Input"].map((category) => (
              <div key={category}>
                <h3 className="font-semibold text-gray-900 mb-3">{category} Modules</h3>
                <div className="space-y-2">
                  {nodeTypes.filter((nodeType) => nodeType.category === category).map((nodeType) => {
                    const Icon = nodeType.icon;
                    return (
                      <Card
                        key={nodeType.type}
                        className={`p-3 cursor-pointer transition-all hover:shadow-md ${nodeType.color}`}
                        draggable
                        onDragStart={(e) => handleModuleDragStart(e, nodeType.type)}
                        onClick={() => {
                          const position = { x: Math.random() * 400 + 100, y: Math.random() * 300 + 100 };
                          const newNode = {
                            id: `${nodeType.type}-${Date.now()}`,
                            type: nodeType.type,
                            title: nodeType.title,
                            description: nodeType.description,
                            icon: nodeType.icon,
                            position,
                            inputs: nodeType.inputs,
                            outputs: nodeType.outputs,
                            status: "idle",
                            config: {},
                          };
                          setNodes((prev) => [...prev, newNode]);
                        }}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
                            <Icon className="w-4 h-4 text-gray-600" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-sm text-gray-900">{nodeType.title}</h4>
                            <p className="text-xs text-gray-600">{nodeType.description}</p>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      </ResponsivePanel>
      {/* Canvas */}
      <div
        className="workflow-canvas flex-1 relative overflow-hidden mt-20"
      ref={canvasRef}
      onDragOver={handleCanvasDragOver}
      onDrop={handleCanvasDrop}
        style={{ background: '#e0e7ef' }}
      >
        {/* Render nodes */}
        {nodes.map((node) => {
          const Icon = node.icon;
          return (
            <div
              key={node.id}
              className="absolute w-56 cursor-move z-10 group"
              style={{ left: node.position.x, top: node.position.y }}
              onMouseDown={(e) => handleNodeMouseDown(e, node.id)}
            >
              <Card className="p-4 border-2 bg-white border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
                    <Icon className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-sm text-gray-900">{node.title}</h4>
                    <p className="text-xs text-gray-600">{node.description}</p>
                  </div>
                  {/* Node actions */}
                  <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={(e) => { e.stopPropagation(); handleConfigNode(node.id); }} className="p-1 hover:bg-gray-100 rounded" title="Configure">
                      <Settings className="w-3 h-3 text-gray-500" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDuplicateNode(node.id); }} className="p-1 hover:bg-gray-100 rounded" title="Duplicate">
                      <Copy className="w-3 h-3 text-gray-500" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteNode(node.id); }} className="p-1 hover:bg-gray-100 rounded" title="Delete">
                      <Trash2 className="w-3 h-3 text-gray-500" />
                    </button>
                  </div>
                </div>
              </Card>
            </div>
            );
          })}
        </div>
    </div>
  );
}
